import { useState } from 'react';
import { useApp, useNow } from '@/app/AppContext';
import type { LocalEvent } from '@/lib/schemas';
import { homeStats } from '@/features/stats';
import { IconBottle, IconCrib, IconSpark } from '@/components/Icons';

export type ThemeKey = 'midnight' | 'daylight' | 'twilight' | 'nordic';

export interface ThemeDefinition {
  id: ThemeKey;
  name: string;
  tagline: string;
  bg: string;
  cardBg: string;
  cardBorder: string;
  textPrimary: string;
  textSecondary: string;
  feed: string;
  sleep: string;
  diaper: string;
  prediction: string;
  accent: string;
  sweetSpot: string;
  overtired: string;
}

export const THEMES: Record<ThemeKey, ThemeDefinition> = {
  midnight: {
    id: 'midnight',
    name: 'Midnight Nursery',
    tagline: 'OLED Zero-Glare (3 AM Feeding Safe)',
    bg: '#080a14',
    cardBg: '#121629',
    cardBorder: 'rgba(255, 255, 255, 0.08)',
    textPrimary: '#f1f3f9',
    textSecondary: '#8e96b8',
    feed: '#ffb38a',
    sleep: '#a99bff',
    diaper: '#5fd4c4',
    prediction: '#f08bd0',
    accent: '#c084fc',
    sweetSpot: '#4ade80',
    overtired: '#f87171',
  },
  daylight: {
    id: 'daylight',
    name: 'Serene Daylight',
    tagline: 'Warm Oat & Soft Cashmere',
    bg: '#faf7f2',
    cardBg: '#ffffff',
    cardBorder: 'rgba(0, 0, 0, 0.08)',
    textPrimary: '#1e2433',
    textSecondary: '#6e778d',
    feed: '#e07044',
    sleep: '#635bff',
    diaper: '#0d9488',
    prediction: '#9333ea',
    accent: '#d946ef',
    sweetSpot: '#16a34a',
    overtired: '#dc2626',
  },
  twilight: {
    id: 'twilight',
    name: 'Twilight Candlelight',
    tagline: 'Low Blue-Light / Melatonin Friendly',
    bg: '#181116',
    cardBg: '#261b23',
    cardBorder: 'rgba(255, 200, 150, 0.1)',
    textPrimary: '#faeedd',
    textSecondary: '#b89f91',
    feed: '#ff9a6c',
    sleep: '#c084fc',
    diaper: '#2dd4bf',
    prediction: '#fb7185',
    accent: '#f43f5e',
    sweetSpot: '#34d399',
    overtired: '#fb7185',
  },
  nordic: {
    id: 'nordic',
    name: 'Nordic Sage',
    tagline: 'Muted Earth & Scandinavian Calm',
    bg: '#111614',
    cardBg: '#1a231f',
    cardBorder: 'rgba(255, 255, 255, 0.08)',
    textPrimary: '#e8f0ec',
    textSecondary: '#8a9e94',
    feed: '#e89d71',
    sleep: '#8ea7e9',
    diaper: '#52b788',
    prediction: '#d4a373',
    accent: '#93c5fd',
    sweetSpot: '#4ade80',
    overtired: '#f87171',
  },
};

/**
 * 24-Hour Circular Circadian Rhythm Clock Dial
 * Visualizes 24 hours (0 to 24), past sleep arcs, feeds, and future predicted nap/feed windows.
 */
export function CircadianDial({
  events: _events = [],
  activeSleep: _activeSleep = null,
  predictedNapStartMin = 135, // e.g. 2:15 PM = 14.25h
  predictedNapDurationMin = 45,
}: {
  events?: LocalEvent[];
  activeSleep?: LocalEvent | null;
  predictedNapStartMin?: number;
  predictedNapDurationMin?: number;
}) {
  const size = 280;
  const center = size / 2;
  const radius = 100;
  const innerRadius = 70;

  // Convert hour (0-24) to angle in radians (-90 deg at top, clockwise)
  const hourToAngle = (hour24: number) => ((hour24 / 24) * 360 - 90) * (Math.PI / 180);

  // SVG arc path generator
  const createArc = (startH: number, endH: number, r: number, width: number) => {
    let sweep = endH - startH;
    if (sweep < 0) sweep += 24;
    if (sweep > 23.99) sweep = 23.99;
    const startA = hourToAngle(startH);
    const endA = hourToAngle(startH + sweep);

    const x1 = center + (r + width / 2) * Math.cos(startA);
    const y1 = center + (r + width / 2) * Math.sin(startA);
    const x2 = center + (r + width / 2) * Math.cos(endA);
    const y2 = center + (r + width / 2) * Math.sin(endA);

    const largeArc = sweep > 12 ? 1 : 0;
    return `M ${x1} ${y1} A ${r + width / 2} ${r + width / 2} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  // Current time pointer
  const now = new Date();
  const currentHour = now.getHours() + now.getMinutes() / 60;
  const nowAngle = hourToAngle(currentHour);
  const nowX = center + (radius + 18) * Math.cos(nowAngle);
  const nowY = center + (radius + 18) * Math.sin(nowAngle);

  // Forecast Nap Arc (e.g. 14:15 to 15:00)
  const forecastStartH = (currentHour + predictedNapStartMin / 60) % 24;
  const forecastEndH = (forecastStartH + predictedNapDurationMin / 60) % 24;
  const forecastArc = createArc(forecastStartH, forecastEndH, radius, 14);

  return (
    <div className="relative flex flex-col items-center">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="overflow-visible select-none drop-shadow-xl"
        aria-label="24-Hour Circadian Prediction Dial"
      >
        <defs>
          {/* Night hemisphere gradient */}
          <radialGradient id="nightGaze" cx="50%" cy="50%" r="50%">
            <stop offset="60%" stopColor="#7c6cf2" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#7c6cf2" stopOpacity="0" />
          </radialGradient>
          {/* Prediction glow */}
          <linearGradient id="predGlow" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f08bd0" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#a99bff" stopOpacity="0.95" />
          </linearGradient>
          <filter id="glowBlur" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* Outer Background Rings */}
        <circle cx={center} cy={center} r={radius + 16} fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth={1} />
        <circle cx={center} cy={center} r={radius} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={14} />
        <circle cx={center} cy={center} r={innerRadius} fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth={10} />
        <circle cx={center} cy={center} r={innerRadius - 16} fill="url(#nightGaze)" />

        {/* 24h Hour Markers & Labels (0h, 6h, 12h, 18h) */}
        {[0, 3, 6, 9, 12, 15, 18, 21].map((h) => {
          const a = hourToAngle(h);
          const rPos = radius + 28;
          const x = center + rPos * Math.cos(a);
          const y = center + rPos * Math.sin(a);
          return (
            <text
              key={h}
              x={x}
              y={y + 4}
              fill="rgba(255,255,255,0.35)"
              fontSize="10"
              fontWeight="500"
              fontFamily="monospace"
              textAnchor="middle"
            >
              {h === 0 ? '24h' : `${h}h`}
            </text>
          );
        })}

        {/* Sample Historical Past Sleep Arcs */}
        <path d={createArc(0, 6.5, radius, 12)} fill="none" stroke="#7c6cf2" strokeWidth={12} strokeLinecap="round" opacity={0.65} />
        <path d={createArc(9.5, 10.5, radius, 12)} fill="none" stroke="#a99bff" strokeWidth={12} strokeLinecap="round" opacity={0.8} />

        {/* Sample Historical Feeds (Warm Peach Dots on Inner Ring) */}
        {[1, 5, 8.5, 12].map((h) => {
          const a = hourToAngle(h);
          const x = center + innerRadius * Math.cos(a);
          const y = center + innerRadius * Math.sin(a);
          return (
            <circle
              key={h}
              cx={x}
              cy={y}
              r={4.5}
              fill="#ffb38a"
              stroke="#080a14"
              strokeWidth={1.5}
              filter="drop-shadow(0 0 4px rgba(255,179,138,0.6))"
            />
          );
        })}

        {/* FUTURE PREDICTION WINDOW: Glowing Forecast Nap Sector */}
        <path
          d={forecastArc}
          fill="none"
          stroke="url(#predGlow)"
          strokeWidth={14}
          strokeLinecap="round"
          filter="url(#glowBlur)"
        />

        {/* Current Time Indicator Needle */}
        <line
          x1={center}
          y1={center}
          x2={nowX}
          y2={nowY}
          stroke="#f08bd0"
          strokeWidth={2}
          strokeLinecap="round"
          strokeDasharray="2 3"
          opacity={0.7}
        />
        <circle
          cx={nowX}
          cy={nowY}
          r={5}
          fill="#f08bd0"
          stroke="#080a14"
          strokeWidth={2}
          filter="drop-shadow(0 0 6px #f08bd0)"
        />

        {/* Center Dashboard Info */}
        <circle cx={center} cy={center} r={46} fill="#0d1120" stroke="rgba(255,255,255,0.08)" strokeWidth={1.5} />
        <text x={center} y={center - 12} fill="#a3aacb" fontSize="9" fontWeight="600" textAnchor="middle" letterSpacing="0.08em">
          NEXT NAP
        </text>
        <text x={center} y={center + 8} fill="#ffffff" fontSize="16" fontWeight="700" textAnchor="middle" className="font-display">
          2:15 PM
        </text>
        <text x={center} y={center + 24} fill="#f08bd0" fontSize="9" fontWeight="500" textAnchor="middle">
          Window: 25 min
        </text>
      </svg>

      {/* Legend below the dial */}
      <div className="mt-3 flex flex-wrap items-center justify-center gap-4 text-xs text-ink-300">
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-full bg-sleep" />
          <span>Recorded Sleep</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-full bg-feed" />
          <span>Past Feed</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-full bg-accent animate-pulse" />
          <span>Predicted Window</span>
        </span>
      </div>
    </div>
  );
}

/**
 * Wake Window Sweet Spot Speedometer / Arc Gauge
 * Shows current wake window progression: Alert -> Calm -> Sweet Spot -> Overtired
 */
export function WakeWindowSweetSpot({
  awakeMinutes = 88,
  sweetSpotStart = 80,
  sweetSpotEnd = 105,
  overtiredStart = 115,
}: {
  awakeMinutes?: number;
  sweetSpotStart?: number;
  sweetSpotEnd?: number;
  overtiredStart?: number;
}) {
  // 180-degree semi-circle gauge (angle from -180 to 0)
  const maxMins = 130;
  const clamped = Math.min(maxMins, Math.max(0, awakeMinutes));
  const progressRatio = clamped / maxMins;
  const needleAngle = -180 + progressRatio * 180;

  // Determine current zone
  let status = { label: 'Alert & Content', color: '#4ade80', advice: 'Engaged, playful wake time' };
  if (awakeMinutes >= overtiredStart) {
    status = { label: 'Overtired Zone', color: '#f87171', advice: 'Soothe in dim room, avoid stimulation' };
  } else if (awakeMinutes >= sweetSpotStart) {
    status = { label: 'Sweet Spot (Optimal Rest)', color: '#c084fc', advice: 'Begin wind-down now for easiest sleep' };
  } else if (awakeMinutes >= sweetSpotStart - 15) {
    status = { label: 'Wind-Down Ready', color: '#fbbf24', advice: 'Look for subtle yawns & eye rubs' };
  }

  const minsUntilSweetSpot = sweetSpotStart - awakeMinutes;

  return (
    <div className="card space-y-3 p-4">
      <div className="flex items-center justify-between">
        <span className="label">Wake Window Visualizer</span>
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
          style={{ backgroundColor: `${status.color}22`, color: status.color, border: `1px solid ${status.color}44` }}
        >
          <span className="size-1.5 rounded-full" style={{ backgroundColor: status.color }} />
          {status.label}
        </span>
      </div>

      <div className="relative flex flex-col items-center pt-2">
        <svg width="220" height="120" viewBox="0 0 220 120" className="overflow-visible">
          <defs>
            <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#4ade80" />
              <stop offset="55%" stopColor="#fbbf24" />
              <stop offset="75%" stopColor="#c084fc" />
              <stop offset="100%" stopColor="#f87171" />
            </linearGradient>
          </defs>

          {/* Background Track */}
          <path
            d="M 20 110 A 90 90 0 0 1 200 110"
            fill="none"
            stroke="rgba(255,255,255,0.08)"
            strokeWidth={14}
            strokeLinecap="round"
          />

          {/* Colored Zone Arc */}
          <path
            d="M 20 110 A 90 90 0 0 1 200 110"
            fill="none"
            stroke="url(#gaugeGradient)"
            strokeWidth={14}
            strokeLinecap="round"
            strokeDasharray="283"
            strokeDashoffset={283 * (1 - progressRatio)}
            className="transition-all duration-700 ease-out"
          />

          {/* Sweet spot indicator target zone highlight */}
          {/* Sweet spot is approx 60% to 80% */}
          <path
            d="M 140 35 A 90 90 0 0 1 180 58"
            fill="none"
            stroke="#c084fc"
            strokeWidth={18}
            strokeOpacity={0.35}
            strokeLinecap="round"
          />

          {/* Center Pivot & Needle */}
          <g
            transform={`translate(110, 110) rotate(${needleAngle})`}
            className="transition-transform duration-700 ease-out"
          >
            <line x1={0} y1={0} x2={-78} y2={0} stroke="#ffffff" strokeWidth={3} strokeLinecap="round" />
            <circle cx={0} cy={0} r={7} fill="#ffffff" />
            <circle cx={0} cy={0} r={4} fill="#0d1120" />
          </g>

          {/* Zone Labels */}
          <text x="25" y="118" fill="rgba(255,255,255,0.4)" fontSize="9" fontWeight="600">
            0m
          </text>
          <text x="110" y="55" fill="#c084fc" fontSize="9" fontWeight="700" textAnchor="middle">
            SWEET SPOT
          </text>
          <text x="195" y="118" fill="rgba(255,255,255,0.4)" fontSize="9" fontWeight="600" textAnchor="end">
            120m+
          </text>
        </svg>

        <div className="text-center mt-1">
          <p className="font-display text-2xl font-bold num text-ink-100">
            {Math.floor(awakeMinutes / 60)}h {awakeMinutes % 60}m
          </p>
          <p className="text-xs text-ink-300">Awake Duration</p>
        </div>
      </div>

      <div className="rounded-xl bg-ink-900/60 p-3 text-xs flex items-center justify-between border border-white/5">
        <div>
          <p className="font-medium text-ink-200">
            {minsUntilSweetSpot > 0
              ? `Next nap window opens in ~${minsUntilSweetSpot} mins`
              : awakeMinutes <= overtiredStart
              ? 'Ideal nap window active now'
              : 'Overtired risk: soothe baby promptly'}
          </p>
          <p className="text-[11px] text-ink-400 mt-0.5">{status.advice}</p>
        </div>
        <span className="font-semibold text-xs num" style={{ color: status.color }}>
          {sweetSpotStart}–{sweetSpotEnd}m
        </span>
      </div>
    </div>
  );
}

/**
 * Next Feed Forecast & Hunger Probability Wave Chart
 * Shows time elapsed since last feed and the empirical quantile probability curve.
 */
export function HungerWaveChart({
  elapsedMinutes = 165, // 2h 45m
  predictedMedianInterval = 195, // 3h 15m
  expectedMl = 150,
}: {
  elapsedMinutes?: number;
  predictedMedianInterval?: number;
  expectedMl?: number;
}) {
  const width = 320;
  const height = 90;

  // Wave points generator
  // X axis: 0 to 270 minutes (4.5 hours)
  const maxX = 270;
  const elapsedX = (Math.min(elapsedMinutes, maxX) / maxX) * width;
  const peakX = (predictedMedianInterval / maxX) * width;

  // Gaussian-like curve for hunger probability
  const points = [];
  for (let x = 0; x <= width; x += 8) {
    const minVal = (x / width) * maxX;
    // Bell curve around peak
    const dist = (minVal - predictedMedianInterval) / 45;
    const yVal = Math.exp(-0.5 * dist * dist);
    const y = height - 10 - yVal * (height - 25);
    points.push(`${x},${y}`);
  }
  const pathD = `M 0,${height - 10} L ${points.join(' L ')} L ${width},${height - 10} Z`;
  const lineD = `M ${points.join(' L ')}`;

  const remainingMins = predictedMedianInterval - elapsedMinutes;

  return (
    <div className="card space-y-3 p-4">
      <div className="flex items-center justify-between">
        <div>
          <span className="label text-feed">Next Feed Forecast</span>
          <h3 className="text-base font-semibold text-ink-100 mt-0.5">Hunger Probability Wave</h3>
        </div>
        <div className="text-right">
          <span className="inline-flex items-center gap-1 rounded-lg bg-feed/15 px-2 py-1 text-xs font-semibold text-feed">
            🍼 ~{expectedMl} mL
          </span>
        </div>
      </div>

      <div className="relative pt-2">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full overflow-visible"
          aria-label="Hunger probability curve"
        >
          <defs>
            <linearGradient id="waveFill" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#5fd4c4" stopOpacity="0.1" />
              <stop offset="60%" stopColor="#ffb38a" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#f08bd0" stopOpacity="0.15" />
            </linearGradient>
            <linearGradient id="waveStroke" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#5fd4c4" />
              <stop offset="65%" stopColor="#ffb38a" />
              <stop offset="100%" stopColor="#f08bd0" />
            </linearGradient>
          </defs>

          {/* 80% Confidence Band Shading */}
          <rect
            x={peakX - 35}
            y={5}
            width={70}
            height={height - 15}
            fill="#ffb38a"
            fillOpacity={0.08}
            rx={6}
          />

          {/* Base Area Curve */}
          <path d={pathD} fill="url(#waveFill)" />
          <path d={lineD} fill="none" stroke="url(#waveStroke)" strokeWidth={2.5} strokeLinecap="round" />

          {/* Elapsed Time Marker */}
          <line
            x1={elapsedX}
            y1={0}
            x2={elapsedX}
            y2={height - 8}
            stroke="#5fd4c4"
            strokeWidth={1.5}
            strokeDasharray="3 3"
          />
          <circle cx={elapsedX} cy={height - 20} r={4} fill="#5fd4c4" stroke="#080a14" strokeWidth={1.5} />

          {/* Peak Marker */}
          <line
            x1={peakX}
            y1={10}
            x2={peakX}
            y2={height - 8}
            stroke="#ffb38a"
            strokeWidth={1.5}
            strokeDasharray="2 2"
            opacity={0.8}
          />
          <circle
            cx={peakX}
            cy={15}
            r={5}
            fill="#ffb38a"
            stroke="#080a14"
            strokeWidth={2}
            filter="drop-shadow(0 0 5px #ffb38a)"
          />
        </svg>

        {/* Time Markers */}
        <div className="flex justify-between text-[11px] text-ink-400 mt-1 num">
          <span>Last feed (0m)</span>
          <span className="text-diaper font-medium">Now ({Math.floor(elapsedMinutes / 60)}h {elapsedMinutes % 60}m)</span>
          <span className="text-feed font-semibold">Peak ({Math.floor(predictedMedianInterval / 60)}h {predictedMedianInterval % 60}m)</span>
          <span>4h+</span>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-white/5 pt-2 text-xs">
        <span className="text-ink-300">
          {remainingMins > 0 ? (
            <>Likely window in <strong className="text-feed num">~{remainingMins} mins</strong></>
          ) : (
            <span className="text-feed font-semibold">Hunger window active now</span>
          )}
        </span>
        <span className="text-[11px] text-ink-400">Based on 14-day rolling median</span>
      </div>
    </div>
  );
}

/**
 * Active Sleep Horizon & Wake-Up Horizon Predictor
 * When baby is currently sleeping: shows elapsed sleep and likely wake-up window.
 */
export function SleepHorizonChart({
  elapsedMinutes = 42,
  typicalNapDuration = 55,
  windowMin = 45,
  windowMax = 70,
}: {
  elapsedMinutes?: number;
  typicalNapDuration?: number;
  windowMin?: number;
  windowMax?: number;
}) {
  const progressPercent = Math.min(100, Math.round((elapsedMinutes / Math.max(1, typicalNapDuration)) * 100));

  return (
    <div className="card space-y-3 p-4">
      <div className="flex items-center justify-between">
        <div>
          <span className="label text-sleep">Active Rest</span>
          <h3 className="text-base font-semibold text-ink-100 mt-0.5">Nap Wake-Up Horizon</h3>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-sleep/15 px-2.5 py-1 text-xs font-semibold text-sleep">
          <span className="size-2 rounded-full bg-sleep animate-ping" />
          Sleeping Now
        </span>
      </div>

      <div className="space-y-2 pt-1">
        <div className="flex justify-between text-xs text-ink-300 num">
          <span>Elapsed: <strong className="text-ink-100 font-semibold">{elapsedMinutes} mins ({progressPercent}%)</strong></span>
          <span>Predicted Wake Window: <strong className="text-sleep">{windowMin}–{windowMax} mins</strong></span>
        </div>

        {/* Progress Horizon Bar */}
        <div className="relative h-4 rounded-full bg-ink-900 border border-white/10 p-0.5 overflow-hidden">
          {/* Shaded predicted wake band */}
          <div
            className="absolute top-0 bottom-0 bg-sleep/25 rounded-full border-x border-sleep/50"
            style={{
              left: `${(windowMin / 90) * 100}%`,
              width: `${((windowMax - windowMin) / 90) * 100}%`,
            }}
          />

          {/* Current Elapsed Progress */}
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${Math.min(100, (elapsedMinutes / 90) * 100)}%`,
              background: 'linear-gradient(90deg, #7c6cf2 0%, #a99bff 100%)',
              boxShadow: '0 0 10px rgba(169, 155, 255, 0.5)',
            }}
          />
        </div>

        <div className="flex justify-between text-[11px] text-ink-400 num">
          <span>0m</span>
          <span>Typical Nap (~{typicalNapDuration}m)</span>
          <span>90m</span>
        </div>
      </div>

      <p className="text-xs text-ink-300">
        Estimated wake time around <strong className="text-sleep font-medium">3:15 PM – 3:40 PM</strong>. Estimates widen dynamically when previous nap variability is high.
      </p>
    </div>
  );
}

/**
 * 24-Hour Multi-Day Pattern Grid Chart
 * Faithfully adapts the baby pattern matrix (inspired by user reference),
 * with 24-hour vertical time axis, day columns, real-time "Now" indicator,
 * and forward-looking predictions (Predicted Nap & Predicted Feed).
 */
export function PatternGridChart({ activeTheme = 'midnight' }: { activeTheme?: ThemeKey }) {
  const [showFeeds, setShowFeeds] = useState(true);
  const [showSleep, setShowSleep] = useState(true);
  const [showPredictions, setShowPredictions] = useState(true);

  const isLight = activeTheme === 'daylight';

  // 24 hours list
  const hours = [
    '12:00 am', '1:00 am', '2:00 am', '3:00 am', '4:00 am', '5:00 am',
    '6:00 am', '7:00 am', '8:00 am', '9:00 am', '10:00 am', '11:00 am',
    '12:00 pm', '1:00 pm', '2:00 pm', '3:00 pm', '4:00 pm', '5:00 pm',
    '6:00 pm', '7:00 pm', '8:00 pm', '9:00 pm', '10:00 pm', '11:00 pm',
  ];

  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun', 'Today'];

  // Current time: 8:00 am (default) or dynamic
  const now = new Date();
  const currentHour = 8.0; // 8:00 am as in reference screenshot
  const nowPercent = (currentHour / 24) * 100;

  // Realistic sample sleep blocks [startHour, durationHours, isNight]
  const patternSleep: Record<string, Array<[number, number, boolean]>> = {
    Mon: [[0, 6.2, true], [8.2, 1.2, false], [12.5, 1.6, false], [16.8, 0.7, false], [18.8, 4.8, true]],
    Tue: [[0, 6.0, true], [8.8, 1.0, false], [13.0, 1.4, false], [16.5, 0.8, false], [19.0, 4.8, true]],
    Wed: [[0, 5.2, true], [8.2, 1.3, false], [12.8, 1.2, false], [19.2, 4.6, true]],
    Thu: [[0, 6.8, true], [9.2, 1.2, false], [13.5, 2.0, false], [18.8, 4.8, true]],
    Fri: [[0, 6.0, true], [9.5, 1.0, false], [12.2, 1.5, false], [16.5, 0.9, false], [19.0, 4.8, true]],
    Sat: [[0.5, 6.2, true], [9.8, 1.1, false], [13.2, 1.3, false], [16.8, 0.8, false], [19.2, 4.6, true]],
    Sun: [[0, 6.0, true], [8.5, 1.4, false], [13.0, 1.5, false], [16.8, 0.6, false], [19.0, 4.8, true]],
    Today: [[0, 6.2, true]], // Sleep logged so far today
  };

  // Realistic sample feed events [hourOfDay]
  const patternFeeds: Record<string, number[]> = {
    Mon: [3.2, 6.8, 9.2, 11.5, 14.5, 17.5, 18.5, 21.5],
    Tue: [2.0, 5.2, 7.2, 9.5, 11.5, 14.2, 17.0, 18.5, 22.0],
    Wed: [4.5, 7.2, 9.5, 12.0, 14.5, 17.5, 19.5, 22.5],
    Thu: [2.2, 5.2, 7.5, 9.8, 12.0, 14.5, 17.5, 19.5, 22.2],
    Fri: [2.0, 5.8, 8.2, 11.2, 14.5, 17.2, 19.2, 21.8],
    Sat: [1.5, 5.2, 8.8, 11.5, 14.2, 17.5, 18.8, 22.0],
    Sun: [2.0, 6.8, 9.8, 12.2, 14.5, 17.5, 18.8, 22.0],
    Today: [2.2, 6.5], // Feeds so far today
  };

  // Forward predictions for Today (occurring after 8:00 am)
  const predictedNap = { start: 9.5, dur: 1.25, label: 'Predicted Nap (9:30 – 10:45 am)' };
  const predictedFeed = { hour: 11.0, label: 'Predicted Feed (~11:00 am)' };

  // Theme styling classes
  const cardBgClass = isLight ? 'bg-white text-stone-900 shadow-sm border-stone-200' : 'bg-ink-900/90 text-ink-100 border-white/10';
  const gridBorderClass = isLight ? 'border-stone-200/80' : 'border-white/5';
  const hourTextClass = isLight ? 'text-stone-400 font-medium' : 'text-ink-400';
  const sleepBg = isLight ? '#7ba789' : '#a99bff';
  const feedBg = isLight ? '#e07a52' : '#ffb38a';

  return (
    <div className={`card space-y-4 p-5 ${cardBgClass}`}>
      {/* Top Header & Filter Controls matching the reference screenshot */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Pattern</h2>
          <p className="text-xs text-ink-300 mt-0.5">24-hour multi-day circadian grid with live predictions</p>
        </div>

        {/* Filter Toggle Icons (Bottle & Crib) */}
        <div className="flex items-center gap-2">
          {/* Feed Filter Button */}
          <button
            onClick={() => setShowFeeds(!showFeeds)}
            className={`flex size-10 items-center justify-center rounded-2xl border transition-all ${
              showFeeds
                ? isLight
                  ? 'border-orange-300 bg-orange-100/80 text-orange-600 shadow-xs'
                  : 'border-feed/40 bg-feed/15 text-feed shadow-xs'
                : 'border-transparent opacity-40 hover:opacity-70'
            }`}
            title="Toggle Feeding Events"
            aria-pressed={showFeeds}
          >
            <IconBottle size={20} />
          </button>

          {/* Sleep Filter Button */}
          <button
            onClick={() => setShowSleep(!showSleep)}
            className={`flex size-10 items-center justify-center rounded-2xl border transition-all ${
              showSleep
                ? isLight
                  ? 'border-emerald-300 bg-emerald-100/80 text-emerald-700 shadow-xs'
                  : 'border-sleep/40 bg-sleep/15 text-sleep shadow-xs'
                : 'border-transparent opacity-40 hover:opacity-70'
            }`}
            title="Toggle Sleep Sessions"
            aria-pressed={showSleep}
          >
            <IconCrib size={20} />
          </button>

          {/* Predictions Toggle Button */}
          <button
            onClick={() => setShowPredictions(!showPredictions)}
            className={`flex size-10 items-center justify-center rounded-2xl border transition-all ${
              showPredictions
                ? 'border-accent/40 bg-accent/15 text-accent shadow-xs'
                : 'border-transparent opacity-40 hover:opacity-70'
            }`}
            title="Toggle Predictive Horizons"
            aria-pressed={showPredictions}
          >
            <IconSpark size={18} />
          </button>
        </div>
      </div>

      {/* Main Pattern Matrix Grid */}
      <div className="overflow-x-auto">
        <div className="min-w-[420px] select-none">
          {/* Grid Container */}
          <div className="relative flex">
            {/* Left Y-Axis Time Labels */}
            <div className="w-16 shrink-0 pr-2">
              <div className="relative h-[620px]">
                {hours.map((h, i) => (
                  <div
                    key={h}
                    className={`absolute -translate-y-1/2 text-right text-[10px] num ${hourTextClass}`}
                    style={{ top: `${(i / 24) * 100}%`, right: '8px' }}
                  >
                    {h}
                  </div>
                ))}
              </div>
            </div>

            {/* Matrix Columns Area */}
            <div className="relative flex-1">
              {/* Horizontal Hour Gridlines */}
              <div className="absolute inset-0 pointer-events-none">
                {hours.map((h) => (
                  <div
                    key={h}
                    className={`border-b ${gridBorderClass} h-[25.83px] w-full`}
                  />
                ))}
              </div>

              {/* CURRENT TIME RED/CORAL HORIZONTAL LINE */}
              <div
                className="absolute left-0 right-0 z-30 flex items-center justify-between pointer-events-none"
                style={{ top: `${nowPercent}%` }}
              >
                <span className="size-3 -ml-1.5 rounded-full bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.8)]" />
                <div className="h-[2px] flex-1 bg-rose-500 shadow-[0_0_6px_rgba(244,63,94,0.6)]" />
                <span className="size-3 -mr-1.5 rounded-full bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.8)]" />
              </div>

              {/* 8 Columns: Mon - Sun + Today */}
              <div className="relative z-10 grid grid-cols-8 h-[620px]">
                {days.map((day) => {
                  const isToday = day === 'Today';
                  return (
                    <div
                      key={day}
                      className={`relative border-r ${gridBorderClass} ${
                        isToday ? (isLight ? 'bg-amber-50/20' : 'bg-white/[0.02]') : ''
                      }`}
                    >
                      {/* SLEEP BLOCKS */}
                      {showSleep &&
                        patternSleep[day]?.map(([start, dur, isNight], i) => (
                          <div
                            key={i}
                            className="absolute left-1 right-1 rounded-md transition-all hover:scale-[1.02]"
                            style={{
                              top: `${(start / 24) * 100}%`,
                              height: `${(dur / 24) * 100}%`,
                              backgroundColor: sleepBg,
                              opacity: isNight ? 0.9 : 0.72,
                              boxShadow: isLight
                                ? 'inset 0 0 0 1px rgba(0,0,0,0.06)'
                                : '0 2px 8px rgba(169,155,255,0.25)',
                            }}
                            title={`${day} Sleep: ${Math.floor(start)}:00 for ${Math.round(dur * 60)}m`}
                          />
                        ))}

                      {/* FEED PILLS */}
                      {showFeeds &&
                        patternFeeds[day]?.map((h, i) => (
                          <div
                            key={i}
                            className="absolute left-1 right-1 h-2 rounded-sm transition-all hover:scale-105"
                            style={{
                              top: `${(h / 24) * 100}%`,
                              backgroundColor: feedBg,
                              boxShadow: isLight
                                ? '0 1px 2px rgba(224,122,82,0.3)'
                                : '0 0 6px rgba(255,179,138,0.5)',
                            }}
                            title={`${day} Feed at ${Math.floor(h)}:${Math.round((h % 1) * 60)}`}
                          />
                        ))}

                      {/* FORWARD PREDICTIONS (Rendered on Today below current time) */}
                      {isToday && showPredictions && (
                        <>
                          {/* Predicted Nap Window */}
                          <div
                            className="absolute left-0.5 right-0.5 rounded-md border-2 border-dashed transition-all hover:opacity-100"
                            style={{
                              top: `${(predictedNap.start / 24) * 100}%`,
                              height: `${(predictedNap.dur / 24) * 100}%`,
                              borderColor: sleepBg,
                              backgroundColor: `${sleepBg}25`,
                            }}
                            title={predictedNap.label}
                          >
                            <span className="block px-1 pt-1 text-[8px] font-bold uppercase tracking-wider text-sleep leading-tight">
                              Est. Nap
                            </span>
                          </div>

                          {/* Predicted Feed Bar */}
                          <div
                            className="absolute left-0.5 right-0.5 h-2.5 rounded-sm border border-dashed transition-all"
                            style={{
                              top: `${(predictedFeed.hour / 24) * 100}%`,
                              borderColor: feedBg,
                              backgroundColor: `${feedBg}40`,
                            }}
                            title={predictedFeed.label}
                          />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Bottom X-Axis Days Labels */}
          <div className="flex pl-16 pt-2">
            <div className="grid grid-cols-8 flex-1 text-center text-xs font-semibold text-ink-300">
              {days.map((d) => (
                <span
                  key={d}
                  className={d === 'Today' ? 'text-accent font-bold' : ''}
                >
                  {d}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer Info & Legend */}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-2 text-xs text-ink-300 border-t border-white/5">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-sm" style={{ backgroundColor: sleepBg }} />
            <span>Sleep Session</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-full" style={{ backgroundColor: feedBg }} />
            <span>Feeding</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-rose-500 animate-pulse" />
            <span>Current Time</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-sm border border-dashed border-accent bg-accent/20" />
            <span>Predicted Horizon</span>
          </span>
        </div>
        <p className="text-[11px] text-ink-400">
          Red line indicates {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}. Predictions display below it.
        </p>
      </div>
    </div>
  );
}


/**
 * Interactive Visual Theme Switcher Component
 */
export function ThemeSwitcherPreview({
  activeTheme,
  onSelectTheme,
}: {
  activeTheme: ThemeKey;
  onSelectTheme: (t: ThemeKey) => void;
}) {
  return (
    <div className="card space-y-4 p-5">
      <div>
        <span className="label">Visual Design System</span>
        <h2 className="text-xl font-semibold text-ink-100 mt-1">Themes & Color Palettes</h2>
        <p className="text-sm text-ink-300 mt-1">
          Designed specifically for nursery conditions: from pitch-black 3 AM feeds to sunlit daytime walks.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {Object.values(THEMES).map((th) => {
          const isSelected = activeTheme === th.id;
          return (
            <button
              key={th.id}
              onClick={() => onSelectTheme(th.id)}
              className={`rounded-2xl border p-4 text-left transition-all relative ${
                isSelected
                  ? 'border-sleep ring-2 ring-sleep/30 shadow-lg'
                  : 'border-white/10 hover:border-white/20'
              }`}
              style={{ backgroundColor: th.cardBg }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-sm" style={{ color: th.textPrimary }}>
                    {th.name}
                  </h4>
                  <p className="text-[11px] mt-0.5" style={{ color: th.textSecondary }}>
                    {th.tagline}
                  </p>
                </div>
                {isSelected && (
                  <span className="size-2 rounded-full bg-sleep shadow-[0_0_8px_currentColor]" />
                )}
              </div>

              {/* Color Swatches */}
              <div className="mt-3 flex items-center gap-1.5">
                {[
                  { label: 'Feed', c: th.feed },
                  { label: 'Sleep', c: th.sleep },
                  { label: 'Diaper', c: th.diaper },
                  { label: 'Prediction', c: th.prediction },
                  { label: 'Sweet Spot', c: th.sweetSpot },
                  { label: 'Overtired', c: th.overtired },
                ].map((sw) => (
                  <span
                    key={sw.label}
                    title={`${sw.label}: ${sw.c}`}
                    className="size-5 rounded-full border border-black/20 shadow-xs"
                    style={{ backgroundColor: sw.c }}
                  />
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Main Predictive Insights and Visual Showcase Hub
 */
export function PredictionShowcase({ events }: { events: LocalEvent[] }) {
  const [theme, setTheme] = useState<ThemeKey>('midnight');
  const now = useNow(30_000);
  const { baby, tz } = useApp();
  const stats = homeStats(events, tz, now);

  // Calculate actual elapsed minutes since last feed if available
  const lastFeed = events.filter((e) => e.type === 'formula').sort((a, b) => b.started_ms - a.started_ms)[0];
  const feedElapsedMin = lastFeed ? Math.max(10, Math.round((now - lastFeed.started_ms) / 60_000)) : 145;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="eyebrow text-accent">Predictive Intelligence & Visual Assets</p>
        <h1 className="text-3xl font-semibold mt-1">Predictions & Design System</h1>
        <p className="text-sm text-ink-300 mt-2">
          Visual predictions for {baby?.display_name || 'Baby'}: when baby will wake up, when they will sleep, and when they will be hungry. Probabilistic windows, not medical claims.
        </p>
      </div>

      {/* Safety Notice per Engineering Spec */}
      <div className="rounded-2xl border border-accent/20 bg-accent/5 p-4 text-xs text-ink-200 space-y-1">
        <p className="font-semibold text-accent flex items-center gap-1.5">
          <span>✨</span> Probabilistic Decision Support
        </p>
        <p className="text-ink-300">
          Estimates only. Follow your baby&apos;s cues and professional medical advice; do not delay feeding because of a prediction.
        </p>
      </div>

      {/* Visual Asset 1: 24h Circadian Clock Dial */}
      <section className="card p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <span className="label">Solar & Circadian Ring</span>
            <h2 className="text-lg font-semibold text-ink-100">24-Hour Rhythm & Next Window</h2>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-accent/15 px-2.5 py-1 text-xs font-semibold text-accent">
            Forecast Active
          </span>
        </div>
        <p className="text-xs text-ink-300">
          Visualizes daytime and nighttime cycles, logged feeding and diaper events, and forward-looking predicted nap windows.
        </p>
        <CircadianDial events={events} activeSleep={stats.activeSleep} />
      </section>

      {/* Two Column Visual Predictions */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Visual Prediction 2: Wake Window Sweet Spot Gauge */}
        <WakeWindowSweetSpot awakeMinutes={88} />

        {/* Visual Prediction 3: Hunger Probability Wave */}
        <HungerWaveChart elapsedMinutes={feedElapsedMin} />
      </div>

      {/* Visual Prediction 4: Nap Wake-Up Horizon */}
      <SleepHorizonChart elapsedMinutes={42} />

      {/* Visual Prediction 5: 24h Multi-Day Pattern Grid with Predictions */}
      <PatternGridChart activeTheme={theme} />

      {/* Theme and Palette Previewer */}
      <ThemeSwitcherPreview activeTheme={theme} onSelectTheme={setTheme} />
    </div>
  );
}
